import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useCRM } from '../context/CRMContext';
import { defaultKnowledgeArticles } from '../data/knowledgeArticles';

const BulletSection = ({ title, icon, items }) => {
  if (!items?.length) return null;

  return (
    <section className="mt-8">
      <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-4 flex items-center gap-2">
        <SafeIcon icon={icon} className="w-4 h-4" /> {title}
      </h3>
      <ul className="space-y-3">
        {items.map((item, index) => (
          <li key={`${title}-${index}`} className="flex gap-3 text-slate-700 font-medium leading-relaxed">
            <span className="text-primary-600 font-black">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

const KnowledgeBase = () => {
  const { knowledgeArticles } = useCRM();
  const articles = knowledgeArticles?.length ? knowledgeArticles : defaultKnowledgeArticles;
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [selectedArticleId, setSelectedArticleId] = useState(null);

  // Sjekk om URL har en artikkel-ID ved oppstart
  useEffect(() => {
    const articleId = searchParams.get('article');
    if (articleId) {
      setSelectedArticleId(articleId);
    }
  }, [searchParams]);

  const categories = useMemo(() => {
    const cats = new Set(articles.map(a => a.category));
    return ['Alle', ...Array.from(cats)];
  }, [articles]);

  const filteredArticles = useMemo(() => {
    return articles.filter(article => {
      const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            article.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            article.content.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'Alle' || article.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [articles, searchQuery, activeCategory]);

  const selectedArticle = useMemo(() => 
    articles.find(a => a.id === selectedArticleId), 
  [selectedArticleId, articles]);

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Kunnskapsbase</h1>
          <p className="text-slate-500 font-medium mt-1">Veiledere, dokumentasjon og hjelp til alt i systemet.</p>
        </div>
        <div className="relative w-full md:w-96">
          <SafeIcon icon="🔍" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input 
            type="text" 
            placeholder="Søk i veilederen..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none font-bold text-sm shadow-sm transition-all" 
          />
        </div>
      </div>

      {/* Categories */}
      <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
        {categories.map(cat => (
          <button 
            key={cat} 
            onClick={() => { setActiveCategory(cat); setSelectedArticleId(null); }}
            className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest whitespace-nowrap transition-all border ${activeCategory === cat ? 'bg-slate-900 text-white border-slate-900 shadow-lg' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Article List */}
        <div className={`${selectedArticle ? 'hidden lg:block lg:col-span-4' : 'col-span-12'} space-y-4`}>
          {filteredArticles.length > 0 ? (
            filteredArticles.map((article) => (
              <motion.div 
                key={article.id}
                layout
                onClick={() => setSelectedArticleId(article.id)}
                className={`p-6 rounded-2xl border cursor-pointer transition-all hover:shadow-md group ${selectedArticleId === article.id ? 'bg-primary-50 border-primary-200 ring-1 ring-primary-500' : 'bg-white border-slate-200 hover:border-primary-300'}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="text-[10px] font-black text-primary-600 uppercase tracking-widest bg-white px-2 py-1 rounded-lg border border-primary-100">
                    {article.category}
                  </span>
                  <SafeIcon icon="➡️" className={`w-4 h-4 text-primary-400 transition-transform ${selectedArticleId === article.id ? 'translate-x-1' : 'opacity-0 group-hover:opacity-100'}`} />
                </div>
                <h3 className={`font-bold text-lg mb-1 ${selectedArticleId === article.id ? 'text-primary-900' : 'text-slate-900'}`}>{article.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed line-clamp-2">{article.description}</p>
              </motion.div>
            ))
          ) : (
            <div className="py-12 text-center bg-white rounded-[2rem] border-2 border-dashed border-slate-200">
              <SafeIcon icon="❓" className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 font-medium">Ingen artikler funnet.</p>
            </div>
          )}
        </div>

        {/* Article Detail View */}
        <div className={`lg:col-span-8 ${!selectedArticle ? 'hidden lg:block' : 'col-span-12'}`}>
          <AnimatePresence mode="wait">
            {selectedArticle ? (
              <motion.div 
                key={selectedArticle.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden"
              >
                <div className="p-8 md:p-10">
                  <button 
                    onClick={() => setSelectedArticleId(null)} 
                    className="lg:hidden mb-6 flex items-center gap-2 text-slate-500 font-bold text-sm hover:text-slate-900"
                  >
                    <SafeIcon icon="⬅️" className="w-4 h-4" /> Tilbake til listen
                  </button>

                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-primary-50 rounded-2xl text-primary-600">
                      <SafeIcon icon="📚" className="w-6 h-6" />
                    </div>
                    <div>
                      <span className="text-xs font-black text-slate-400 uppercase tracking-widest">{selectedArticle.category}</span>
                      <h1 className="text-3xl font-black text-slate-900 tracking-tight">{selectedArticle.title}</h1>
                    </div>
                  </div>

                  <div className="prose prose-slate max-w-none">
                    <p className="text-lg text-slate-600 font-medium leading-relaxed mb-8">
                      {selectedArticle.content}
                    </p>

                    {selectedArticle.steps && (
                      <div className="bg-slate-50 rounded-[2rem] p-8 border border-slate-100">
                        <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-6 flex items-center gap-2">
                          <SafeIcon icon="👣" className="w-4 h-4" /> Steg for steg
                        </h3>
                        <div className="space-y-6">
                          {selectedArticle.steps.map((step, index) => (
                            <div key={index} className="flex gap-4">
                              <div className="flex-shrink-0 w-8 h-8 bg-white border-2 border-primary-100 text-primary-600 rounded-full flex items-center justify-center font-black text-sm shadow-sm">
                                {index + 1}
                              </div>
                              <div className="pt-1">
                                <p className="text-slate-700 font-medium">{step}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <BulletSection title="Tips" icon="💡" items={selectedArticle.tips} />
                    <BulletSection title="Vanlige feil" icon="⚠️" items={selectedArticle.commonMistakes} />
                    <BulletSection title="Beste praksis" icon="✅" items={selectedArticle.bestPractices} />

                    {selectedArticle.summary && (
                      <div className="mt-8 rounded-2xl border border-emerald-100 bg-emerald-50 p-5">
                        <h3 className="text-sm font-black text-emerald-900 uppercase tracking-widest mb-2">Kort oppsummering</h3>
                        <p className="text-emerald-900 font-bold leading-relaxed">{selectedArticle.summary}</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-10 pt-10 border-t border-slate-100 flex justify-between items-center">
                    <p className="text-xs font-bold text-slate-400">Var dette nyttig?</p>
                    <div className="flex gap-2">
                      <button className="px-4 py-2 bg-slate-50 hover:bg-emerald-50 text-slate-600 hover:text-emerald-600 rounded-xl text-xs font-black uppercase tracking-widest transition-all">
                        👍 Ja
                      </button>
                      <button className="px-4 py-2 bg-slate-50 hover:bg-rose-50 text-slate-600 hover:text-rose-600 rounded-xl text-xs font-black uppercase tracking-widest transition-all">
                        👎 Nei
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-12 bg-slate-50 rounded-[2.5rem] border border-slate-200 border-dashed opacity-75">
                <div className="w-24 h-24 bg-white rounded-[2rem] flex items-center justify-center shadow-sm mb-6">
                  <SafeIcon icon="👈" className="w-10 h-10 text-slate-300" />
                </div>
                <h3 className="text-xl font-black text-slate-900">Velg en artikkel</h3>
                <p className="text-slate-500 mt-2 max-w-xs">Klikk på en veileder i listen til venstre for å lese innholdet.</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase;